import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Library, Clock, Dumbbell } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import ExercisePickerDialogV2 from "@/components/exercises/ExercisePickerDialogV2";

// Estimate duration: each set ~45s work + rest time, plus 30s transition between exercises
function estimateDuration(exercises) {
  if (!exercises.length) return 0;
  return exercises.reduce((total, ex) => {
    const sets = Number(ex.sets) || 3;
    const rest = Number(ex.rest_seconds) || 60;
    return total + sets * 45 + (sets - 1) * rest + 30;
  }, 0);
}

export default function WorkoutForm({ open, onOpenChange, onSubmit, initialData }) {
  const [showPicker, setShowPicker] = useState(false);
  const { data: allExercises = [] } = useQuery({
    queryKey: ["exercises"],
    queryFn: () => base44.entities.Exercises.list("name", 2500),
    enabled: open,
  });
  const exerciseByName = useMemo(() => {
    const m = new Map();
    for (const e of allExercises) {
      if (e?.name) m.set(e.name.trim().toLowerCase(), e);
    }
    return m;
  }, [allExercises]);

  const [form, setForm] = useState(initialData || {
    name: "",
    description: "",
    difficulty: "intermediate",
    goal: "general_fitness",
    body_part_category: "",
    days_per_week: 3,
    exercises: [],
    is_active: false,
    visible_on_community: false,
  });

  useEffect(() => {
    if (open && initialData) {
      setForm({ name: "", description: "", difficulty: "intermediate", goal: "general_fitness", body_part_category: "", days_per_week: 3, exercises: [], is_active: false, visible_on_community: false, ...initialData });
    } else if (open && !initialData) {
      setForm({ name: "", description: "", difficulty: "intermediate", goal: "general_fitness", body_part_category: "", days_per_week: 3, exercises: [], is_active: false, visible_on_community: false });
    }
  }, [open, initialData]);

  const addExercise = () => {
    setForm(f => ({
      ...f,
      exercises: [...f.exercises, { name: "", sets: 3, reps: "10", rest_seconds: 60, notes: "" }],
    }));
  };

  const addFromLibrary = (ex) => {
    setForm(f => ({
      ...f,
      exercises: [...f.exercises, {
        name: ex.name,
        sets: 3,
        reps: "10",
        rest_seconds: 60,
        notes: "",
        exerciseId: ex.exerciseId,
        gifUrl: ex.gifUrl,
      }],
    }));
  };

  const updateExercise = (idx, field, value) => {
    const updated = [...form.exercises];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm(f => ({ ...f, exercises: updated }));
  };

  const removeExercise = (idx) => {
    setForm(f => ({ ...f, exercises: f.exercises.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(form);
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{initialData?.id ? "Edit Workout" : initialData ? "Create from Template" : "Create Workout Plan"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Plan Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Push Pull Legs" required />
            </div>
            <div>
              <Label>Difficulty</Label>
              <Select value={form.difficulty} onValueChange={(v) => setForm({ ...form, difficulty: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">Beginner</SelectItem>
                  <SelectItem value="intermediate">Intermediate</SelectItem>
                  <SelectItem value="advanced">Advanced</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Goal</Label>
              <Select value={form.goal} onValueChange={(v) => setForm({ ...form, goal: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="strength">Strength</SelectItem>
                  <SelectItem value="hypertrophy">Hypertrophy</SelectItem>
                  <SelectItem value="endurance">Endurance</SelectItem>
                  <SelectItem value="fat_loss">Fat Loss</SelectItem>
                  <SelectItem value="general_fitness">General Fitness</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Target Body Part</Label>
              <Select value={form.body_part_category || ""} onValueChange={(v) => setForm({ ...form, body_part_category: v })}>
                <SelectTrigger><SelectValue placeholder="Select body part..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="chest">Chest</SelectItem>
                  <SelectItem value="back">Back</SelectItem>
                  <SelectItem value="shoulders">Shoulders</SelectItem>
                  <SelectItem value="arms">Arms</SelectItem>
                  <SelectItem value="upper_body">Upper Body</SelectItem>
                  <SelectItem value="upper_arms">Upper Arms</SelectItem>
                  <SelectItem value="lower_arms">Lower Arms</SelectItem>
                  <SelectItem value="legs">Legs</SelectItem>
                  <SelectItem value="upper_legs">Upper Legs</SelectItem>
                  <SelectItem value="lower_legs">Lower Legs</SelectItem>
                  <SelectItem value="core">Core</SelectItem>
                  <SelectItem value="waist">Waist</SelectItem>
                  <SelectItem value="hips">Hips</SelectItem>
                  <SelectItem value="neck">Neck</SelectItem>
                  <SelectItem value="cardio">Cardio</SelectItem>
                  <SelectItem value="full_body">Full Body</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-display">Exercises</Label>
              <div className="flex gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowPicker(true)} className="gap-1">
                  <Library className="w-3 h-3" /> From Library
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={addExercise} className="gap-1">
                  <Plus className="w-3 h-3" /> Manual
                </Button>
              </div>
            </div>
            {form.exercises.map((ex, i) => {
              const fullEx = exerciseByName.get((ex.name || "").trim().toLowerCase());
              const gif = ex.gifUrl || ex.gif_url || fullEx?.gifUrl || fullEx?.gif_url || fullEx?.video_url;
              const targets = (Array.isArray(ex.targetMuscles) && ex.targetMuscles.length > 0)
                ? ex.targetMuscles
                : (Array.isArray(fullEx?.targetMuscles) ? fullEx.targetMuscles : []);

              return (
                <div key={i} className="p-3 rounded-xl bg-muted/50 space-y-2">
                  {gif && (
                    <div className="flex items-center gap-2 pb-1">
                      <img src={gif} alt={ex.name} className="w-10 h-10 rounded-lg object-cover bg-muted" />
                      {targets.length > 0 && (
                        <span className="text-xs text-muted-foreground capitalize">{targets.join(", ")}</span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Input className="flex-1" value={ex.name} onChange={(e) => updateExercise(i, "name", e.target.value)} placeholder="Exercise name" />
                    <Button type="button" variant="ghost" size="icon" title="Pick from library" onClick={() => setShowPicker(true)} className="shrink-0 text-primary">
                      <Library className="w-4 h-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeExercise(i)} className="text-destructive shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Input type="number" value={ex.sets} onChange={(e) => updateExercise(i, "sets", parseInt(e.target.value))} placeholder="Sets" />
                    <Input value={ex.reps} onChange={(e) => updateExercise(i, "reps", e.target.value)} placeholder="Reps" />
                    <Input type="number" value={ex.rest_seconds} onChange={(e) => updateExercise(i, "rest_seconds", parseInt(e.target.value))} placeholder="Rest (s)" />
                  </div>
                </div>
              );
            })}
          </div>
          {/* Session Preview */}
          {form.exercises.length > 0 && (
            <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                  <Dumbbell className="w-4 h-4" />
                  Session Preview
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3" />
                  ~{Math.round(estimateDuration(form.exercises) / 60)} min
                </div>
              </div>
              <div className="space-y-1">
                {form.exercises.map((ex, i) => {
                  const fullEx = exerciseByName.get((ex.name || "").trim().toLowerCase());
                  const gif = ex.gifUrl || ex.gif_url || fullEx?.gifUrl || fullEx?.gif_url || fullEx?.video_url;
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      {gif
                        ? <img src={gif} alt={ex.name} className="w-7 h-7 rounded object-cover shrink-0" />
                        : <div className="w-7 h-7 rounded bg-muted shrink-0" />
                      }
                      <span className="flex-1 truncate capitalize">{ex.name || <span className="text-muted-foreground italic">Unnamed</span>}</span>
                      <span className="text-xs text-muted-foreground shrink-0">{ex.sets || 3}×{ex.reps || "10"}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <Label className="font-medium">Share to Community</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Make this plan visible to all members</p>
            </div>
            <Switch
              checked={!!form.visible_on_community}
              onCheckedChange={(v) => setForm(f => ({ ...f, visible_on_community: v }))}
            />
          </div>

          <Button type="submit" className="w-full">{initialData?.id ? "Update Plan" : "Create Plan"}</Button>
        </form>
      </DialogContent>
    </Dialog>

    <ExercisePickerDialogV2
      open={showPicker}
      onOpenChange={setShowPicker}
      onSelect={(ex) => addFromLibrary(ex)}
    />
    </>
  );
}